// Admin panel for the promo-waitlist blast.
//
// Two-step UX:
//   1. Load the waitlist for CALM100 (or any code passed in) → shows count
//      and full email list so the admin knows what they're firing.
//   2. Dry-run OR live send. Dry-run just returns the recipient list from
//      the server without touching Resend, so the admin can eyeball
//      addresses one more time. Live send emails everyone and marks each
//      row with `last_notified_at` + `notify_count` so re-runs can be
//      audited later.
//
// Only shows when the current user is admin (parent AdminDashboard already
// enforces that at the route level).

import { useEffect, useState } from "react";
import axios from "axios";
import { Send, Users, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PromoWaitlistPanel = ({ code = "CALM100" }) => {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [redemptionsRemaining, setRedemptionsRemaining] = useState(null);
  const [redemptionsMax, setRedemptionsMax] = useState(null);
  const [spotsOpening, setSpotsOpening] = useState("");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);

  const loadWaitlist = async () => {
    setLoading(true);
    try {
      const [waitRes, redeemRes] = await Promise.all([
        axios.get(`${API}/promo/admin/waitlist/${code}`, { withCredentials: true }),
        axios.get(`${API}/promo/admin/redemptions/${code}`, { withCredentials: true }),
      ]);
      setEntries(waitRes.data.entries || []);
      setRedemptions(redeemRes.data.redemptions || []);
      setRedemptionsRemaining(redeemRes.data.remaining);
      setRedemptionsMax(redeemRes.data.max_uses);
    } catch (err) {
      toast.error("Failed to load waitlist / redemptions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadWaitlist(); }, [code]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleDryRun = async () => {
    setSending(true);
    setLastResult(null);
    try {
      const r = await axios.post(
        `${API}/promo/admin/waitlist/notify`,
        {
          code,
          dry_run: true,
          spots_opening: spotsOpening ? Number.parseInt(spotsOpening, 10) : null,
        },
        { withCredentials: true }
      );
      setDryRunResult(r.data);
      toast.info(`Dry run: would send to ${r.data.would_send} recipients`);
    } catch (err) {
      toast.error("Dry run failed");
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    if (!window.confirm(
      `Send the "spot opened up" email to ${entries.length} recipient${entries.length === 1 ? "" : "s"}?`
    )) return;
    setSending(true);
    setDryRunResult(null);
    try {
      const r = await axios.post(
        `${API}/promo/admin/waitlist/notify`,
        {
          code,
          dry_run: false,
          spots_opening: spotsOpening ? Number.parseInt(spotsOpening, 10) : null,
        },
        { withCredentials: true }
      );
      setLastResult(r.data);
      toast.success(`Sent ${r.data.sent} email${r.data.sent === 1 ? "" : "s"} (${r.data.failed} failed)`);
      await loadWaitlist();
    } catch (err) {
      const detail = err?.response?.data?.detail || "send_failed";
      toast.error(`Send failed: ${detail}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="promo-waitlist-panel">
      {/* ── Who claimed CALM100 ─ primary answer to "who used my code?" ── */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F6C071] to-[#E29B4C] flex items-center justify-center">
            <span className="text-white text-lg">👑</span>
          </div>
          <div>
            <h3 className="font-outfit font-semibold text-lg text-[#1B2349]">
              {code} Redemptions
            </h3>
            <p className="text-sm text-[#718096]">
              People who have claimed lifetime free access via this code.
            </p>
          </div>
          <div className="ml-auto text-right">
            <div className="text-3xl font-semibold text-[#1B2349]" data-testid="redemptions-count">
              {loading ? "…" : `${redemptions.length}${redemptionsMax != null ? ` / ${redemptionsMax}` : ""}`}
            </div>
            <div className="text-xs uppercase tracking-wider text-[#718096]">redeemed</div>
          </div>
        </div>
        {loading ? (
          <div className="text-sm text-[#718096]">Loading…</div>
        ) : redemptions.length === 0 ? (
          <div className="text-sm text-[#718096]">
            No one has claimed the code yet. As people redeem CALM100 they'll appear here in order.
          </div>
        ) : (
          <ul className="space-y-1.5 max-h-72 overflow-y-auto border-t border-slate-100 pt-3" data-testid="redemption-entries">
            {redemptions.map((u, idx) => (
              <li key={u.user_id} className="flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-slate-50">
                <span className="text-[#1B2349]">
                  <span className="text-[#718096] text-xs mr-2">#{idx + 1}</span>
                  <span className="font-medium">{u.name || "(no name)"}</span>
                  <span className="text-[#718096] ml-2">{u.email}</span>
                </span>
                <span className="text-xs text-[#718096]">
                  {u.vip_activated_at ? new Date(u.vip_activated_at).toLocaleDateString() : ""}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Waitlist ─ only meaningful once the 100 spots are gone ─ */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[#FEE2E2] flex items-center justify-center">
            <Users className="w-5 h-5 text-[#E11D48]" />
          </div>
          <div>
            <h3 className="font-outfit font-semibold text-lg text-[#1B2349]">
              {code} Waitlist
            </h3>
            <p className="text-sm text-[#718096]">
              Emails captured AFTER the 100 spots are gone. Empty right now — good sign, means spots still remain.{" "}
              <button onClick={loadWaitlist} className="underline underline-offset-2 hover:text-[#3090A8]" data-testid="waitlist-refresh-btn">
                Refresh
              </button>
            </p>
          </div>
          <div className="ml-auto text-right">
            <div className="text-3xl font-semibold text-[#1B2349]" data-testid="waitlist-count">
              {loading ? "…" : entries.length}
            </div>
            <div className="text-xs uppercase tracking-wider text-[#718096]">on the list</div>
          </div>
        </div>

        {/* Spots opening + action buttons. */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end mb-5">
          <div className="flex-1">
            <label htmlFor="waitlist-spots-input" className="block text-xs font-medium text-[#4A5568] mb-1">
              Spots opening (optional — falls back to current remaining)
            </label>
            <Input
              id="waitlist-spots-input"
              type="number"
              min="1"
              placeholder="e.g. 10"
              value={spotsOpening}
              onChange={(e) => setSpotsOpening(e.target.value)}
              data-testid="waitlist-spots-input"
              className="max-w-[200px]"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleDryRun}
              disabled={sending || entries.length === 0}
              data-testid="waitlist-dry-run-btn"
              className="rounded-full"
            >
              Preview only
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || entries.length === 0}
              className="rounded-full bg-[#1B2349] text-white hover:bg-[#0F1838]"
              data-testid="waitlist-send-btn"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Send blast to {entries.length}</>
              )}
            </Button>
          </div>
        </div>

        {/* Results panel. */}
        {lastResult && (
          <div
            className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mb-4"
            data-testid="waitlist-last-result"
          >
            <div className="flex items-start gap-2 text-emerald-800">
              <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-sm">
                  Sent {lastResult.sent} email{lastResult.sent === 1 ? "" : "s"} · {lastResult.failed} failed · {lastResult.spots_opening} spots mentioned
                </div>
                {lastResult.failures?.length > 0 && (
                  <div className="text-xs text-emerald-900 mt-1">
                    Failed: {lastResult.failures.join(", ")}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {dryRunResult && (
          <div
            className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-4 text-sm"
            data-testid="waitlist-dry-run-result"
          >
            <div className="flex items-start gap-2 text-amber-800">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Dry run — nothing was sent</div>
                <div>Would send to <strong>{dryRunResult.would_send}</strong> recipient{dryRunResult.would_send === 1 ? "" : "s"}.</div>
              </div>
            </div>
          </div>
        )}

        {/* Recipient list. */}
        <div className="border-t border-slate-100 pt-4">
          <div className="text-xs uppercase tracking-wider text-[#718096] mb-2">Recipients</div>
          {loading ? (
            <div className="text-sm text-[#718096]">Loading…</div>
          ) : entries.length === 0 ? (
            <div className="text-sm text-[#718096]">
              No one on the waitlist yet. The banner only starts capturing emails once CALM100 is fully redeemed.
            </div>
          ) : (
            <ul className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="waitlist-entries">
              {entries.map((e) => (
                <li key={e.email} className="flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-slate-50">
                  <span className="text-[#1B2349]">{e.email}</span>
                  <span className="text-xs text-[#718096]">
                    {e.notify_count ? `Notified ${e.notify_count}×` : "New"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromoWaitlistPanel;
